import React, { useMemo, useRef, useState } from "react";
import "./welcome.scss";
import { Trans, useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../store/index.ts";
import IconArrow from "../../../mui/JSXIcons/IconArrow.jsx";
import ShowContainer from "../../../mui/ShowContainer.jsx";
import { API_CASINO } from "../../../../api/casino.js";
import { ENUM_USER_ACTIVE, ENUM_WELCOME_USER_STATUS } from "../../../../store/enums.js";
import RefuseBonusModal from "../../../modal/refuse_bonus/RefuseBonusModal.jsx";
import useBonusHelper from "../../../../hooks/other/useBonusHelper.js";
import CircleInfo from "../../../mui/circle-info/CircleInfo.jsx";
import WelcomeBonusStep from "./WelcomeBonusStep.jsx";
import CusButton from "../../../mui/custom_button/CusButton.jsx";
import { setModalLogin } from "../../../../store/siteSlice.js";
import { useDispatch } from "react-redux";

const testStatusByStatus = {
  [ENUM_WELCOME_USER_STATUS.accrued]: ENUM_WELCOME_USER_STATUS.accrued,
  [ENUM_WELCOME_USER_STATUS.disabled]: ENUM_WELCOME_USER_STATUS.disabled,
  [ENUM_WELCOME_USER_STATUS.enabled]: ENUM_WELCOME_USER_STATUS.enabled,
  [ENUM_WELCOME_USER_STATUS.expired]: ENUM_WELCOME_USER_STATUS.expired,
  [ENUM_WELCOME_USER_STATUS.wagered]: ENUM_WELCOME_USER_STATUS.wagered,
  [ENUM_WELCOME_USER_STATUS.wait]: "Waiting for wagering"
};

export default function Welcome() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const welcome = useAppSelector((state) => state.promotions.welcome);
  const currency = useAppSelector((state) => state.promotions.currency);
  const isAuth = useAppSelector((state) => state.user.isAuth);

  const activeTo = welcome?.activeTo;
  const useFSCampaign = welcome?.useFreespinCampaign;
  const userActivate = welcome?.userActivate || {};
  const userActivateEnable = userActivate?.enabled;
  const activateStatus = userActivate?.status;

  const [openRefuse, setOpenRefuse] = useState(false);
  const [countUnavailable, setCountUnavailable] = useState(0);

  const { setBtnLoading, thenRequest } = useBonusHelper();

  const isLeftAvailable = useRef(false);
  const isNoExpired = activeTo && new Date(activeTo) < new Date();

  const welcomeBonuses = useMemo(() => {
    const struct = [];
    if (!welcome?.enabled) return struct;

    setCountUnavailable(0);
    welcome?.packets?.forEach((pkg, indx) => {
      if (Array.isArray(pkg)) return;
      const title = "Bonus: " + (indx + 1);
      const statusForUser = pkg.statusForUser;
      if (statusForUser === ENUM_WELCOME_USER_STATUS.disabled) {
        setCountUnavailable((prev) => prev + 1);
      }
      const steppers = [];
      pkg?.values?.forEach((valueInfo) => {
        steppers.push(valueInfo);
      });

      const curPkg = {
        title,
        statusForUser,
        steppers,
        wagerAmount: pkg.wagerAmount,
        sumBonus: pkg.bonusAmount
      };
      struct.push(curPkg);
    });

    return struct;
  }, [welcome]);

  const onCancelBtn = () => {
    const bonusId = welcome.id;
    const status = ENUM_USER_ACTIVE.disabled;

    setBtnLoading(true);

    API_CASINO.bonus
      .setActive({ bonusId, status, currency: currency })
      .then(() => {
        thenRequest(status);
      })
      .catch(console.log);
  };

  const isUnnecessary = isNoExpired;
  const articleClass = isUnnecessary ? "unnecessary" : "";
  const hideForUser =
    countUnavailable == welcomeBonuses.length &&
    [ENUM_WELCOME_USER_STATUS.disabled, ENUM_WELCOME_USER_STATUS.expired].includes(
      welcome?.statusForUser
    );

  return (
    <>
      <ShowContainer condition={welcomeBonuses.length && !hideForUser}>
        <article className={`welcome__article ${articleClass}`}>
          <h3>{t("Welcome bonus")}</h3>
          {/* <h5 className={statusForUser}>{t(statusForUser)}</h5> */}
          <section className="bonuses__welcome">
            {welcomeBonuses.map((bonus, indx) => {
              if (!indx) isLeftAvailable.current = false;
              const statusForUser = bonus.statusForUser;

              const showStatus = !(
                [ENUM_USER_ACTIVE.wait, ENUM_USER_ACTIVE.any].includes(activateStatus) &&
                statusForUser === ENUM_WELCOME_USER_STATUS.disabled
              );
              const status = isLeftAvailable.current
                ? "Closed"
                : testStatusByStatus[statusForUser] || statusForUser;

              const statusClass = isLeftAvailable.current ? "closed" : statusForUser;

              let sumBonus = 0;
              let wagerAmount = 0;
              if (statusForUser === ENUM_WELCOME_USER_STATUS.wait && bonus.wagerAmount) {
                sumBonus = bonus.sumBonus;
                wagerAmount = bonus.wagerAmount;
              }

              if (statusForUser === ENUM_WELCOME_USER_STATUS.enabled) {
                isLeftAvailable.current = true;
              }

              return (
                <div className="bonus bonus__welcome" key={indx}>
                  <h4>{t(bonus.title)}</h4>
                  <div className="bonus__welcome__stepper">
                    {bonus?.steppers?.map((currencyInfo, indx) => (
                      <WelcomeBonusStep
                        isFSCampaign={useFSCampaign}
                        currencyInfo={currencyInfo}
                        key={indx}
                        userActiveStatus={activateStatus}
                        statusForUser={statusForUser}
                        // setOpenActiveBonus={setOpenActiveBonus}
                      />
                    ))}
                  </div>

                  <ShowContainer condition={showStatus}>
                    <div className="bonus__bottom">
                      <ShowContainer condition={sumBonus}>
                        <p className="bonus__summa">
                          <span>
                            {sumBonus} {currency}
                          </span>
                          <span>
                            <CircleInfo
                              info={
                                <Trans
                                  i18nKey="You need to place bets for the amount to win back the bonus"
                                  values={{ value: wagerAmount, currency }}
                                  components={{ strong: <strong className="accent" /> }}
                                />
                              }
                            />
                          </span>
                        </p>
                      </ShowContainer>
                      <p className={`status-for-user ${statusClass}`}>{t(status)} </p>
                    </div>
                  </ShowContainer>
                  <ShowContainer condition={indx + 1 < welcomeBonuses.length}>
                    <div className="arrow__next">
                      <IconArrow />
                    </div>
                  </ShowContainer>
                </div>
              );
            })}
          </section>

          <div className="welcome__activate">
            {userActivateEnable && !isNoExpired ? (
              <div className="welcome__activate__time">
                <span>{t("You declined the bonus")}</span>
              </div>
            ) : !isAuth ? (
              <CusButton
                color="green3"
                text="Activate"
                onClick={() => dispatch(setModalLogin(true))}
              />
            ) : (
              <div className="bonus__activated__btn">
                <span className="cancel" onClick={() => setOpenRefuse(true)}>
                  {t("Refuse the bonus")}
                </span>
              </div>
            )}
          </div>
        </article>
      </ShowContainer>

      <RefuseBonusModal
        isOpen={openRefuse}
        closeModal={() => setOpenRefuse(false)}
        bonusName={t("Welcome bonus")}
        onConfirm={() => {
          onCancelBtn();
          setOpenRefuse(false);
        }}
      />
    </>
  );
}
